<script setup>
import { usePageHead } from '@/composables/usePageHead'
import FormAssemblyInline from '@/components/FormAssemblyInline.vue'
import RichTextBlock from '@/components/content/RichTextBlock.vue'
import content from '@/content/contact.yml'
usePageHead(content)
</script>

<template>
  <div class="contact container">
    <h1 class="page-title">{{ content.intro.heading }}</h1>

    <!-- Self-serve deflection notice, full content width. Its lead line is bold
         text (not a heading) so the page keeps a single H1 above it. -->
    <section class="notice">
      <RichTextBlock :block="{ body: content.notice.body }" />
    </section>

    <section class="contact-form">
      <FormAssemblyInline :formId="content.form.formId" />
    </section>

    <!-- Postal and street address, closing the page below the form in a panel matching the
         .notice one above. Two groups (postal, then street) rather than one flat list — they
         are separate addresses, joined by the content's `separator` ("or") on its own line;
         one <span> per supplied line so the breaks are explicit content, not markdown
         hard-break syntax.
         The box is the wrapping <section>, not the <address> itself: the HTML content model
         for <address> excludes heading and sectioning content, and keeping the "Mail" label
         outside it leaves <address> holding only real address content. "or" does sit inside,
         since it belongs in the reading order between the two addresses. -->
    <section v-if="content.address" class="mail">
      <p class="lead">{{ content.address.heading }}</p>
      <address>
        <template v-for="(group, i) in content.address.groups" :key="i">
          <p v-if="i && content.address.separator" class="sep">{{ content.address.separator }}</p>
          <p><span v-for="line in group.lines" :key="line">{{ line }}</span></p>
        </template>
      </address>
    </section>
  </div>
</template>

<style lang="scss" scoped>
.contact { padding-block: $space-8; }
.page-title { margin: 0 0 $space-6; }

// Mail panel closing the page below the form card. The four box declarations are copied
// verbatim from .notice below so the page's two panels are identical by construction —
// only the margin flips to the top, since this one closes the page rather than opening it.
// The $space-6 padding is also what puts the address text on the same vertical rule as the
// notice panel's body text.
.mail {
  margin-top: $space-8;
  padding: $space-6;
  background: var(--color-surface-warm);
  border: 1px solid var(--color-border-decorative);
  @include rounded($radius-lg);

  // Bold body text, not a heading — mirrors the .notice panel's opening line and keeps the
  // page's single H1.
  .lead { margin: 0 0 $space-4; font-weight: 600; }

  address {
    font-style: normal; // undo the UA italic on <address>
    line-height: $lh-base;
    color: var(--color-ink);

    span { display: block; }

    // $space-4 rather than a full blank row: "or" now does the separating work.
    p {
      margin: 0;
      + p { margin-top: $space-4; }
    }
  }
}

// Warm deflection panel — mirrors DownloadsBlock's treatment. Spans the full
// content width.
.notice {
  margin-bottom: $space-8;
  padding: $space-6;
  background: var(--color-surface-warm);
  border: 1px solid var(--color-border-decorative);
  @include rounded($radius-lg);
}

// The FormAssembly form is injected into the page DOM at runtime, so it carries no
// scoped data-v-* attribute — the shared fa-form-fields mixin reaches it with :deep().
// This view owns the form-column measure, the card, and which FA chrome to hide; the field,
// input, validation, and submit-button styling is the shared mixin (src/styles/_mixins.scss).
//
// Framed on a warm card (the site's `--color-section-alt` band) like the newsletter forms'
// card, so the white inputs read clearly against a tinted surface instead of washing out on
// the cream page. Uses the same hairline + radius as the .notice panel above for a coherent
// two-panel page.
.contact-form {
  max-width: 640px; // readable measure for the form column
  padding: $space-6;
  background: var(--color-section-alt);
  border: 1px solid var(--color-border-decorative);
  @include rounded($radius-lg);

  // Hide FA chrome we don't want on the contact form
  :deep(.wFormHeader),
  :deep(.wFormFooter),
  :deep(.supportInfo) { display: none; }

  @include fa-form-fields;

  // Same submit-button fix as StayInformed.vue's newsletter forms: FA's own layout gives
  // the submit button/its wrapping .actions block extra top margin that the mixin's own
  // spacing doesn't account for, and the pill shape (not the mixin's default $radius) is
  // the button style actually wanted here — kept as a per-consumer override rather than
  // changed in the shared mixin, since that would also change Home/Academy's button.
  //
  // Must sit AFTER @include fa-form-fields. The mixin's own `:deep(input[type="submit"])`
  // rule compiles to exactly the same specificity (0,3,1) as this one, so the tiebreak is
  // source order — with this block above the include the mixin won and the button rendered
  // at $radius (16px) instead of the pill, while StayInformed's equivalent override escaped
  // only because it is nested one level deeper inside `.fa-inline`.
  :deep(input[type="submit"]) {
    margin-top: 0px !important;
    border-radius: $radius-pill;
  }
  :deep(div.actions[data-contentid="submit_button"]) { margin-top: 0px !important; }
}
</style>
